import { useCallback, useContext } from 'react';
import { useLocale } from '@hooks/useLocale';
import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime';
import { usePathname, useRouter as useAppRouter } from 'next/navigation';
import { HookError } from '../../errorHandlers';
import { useCustomer } from '@adapters/bigcommerce/hooks/customer/index';

export const logoutFetcher = async (locale: string) =>
  fetch('/api/customer/logout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': locale,
    },
  });

const useLogout = () => {
  // Dual router compatibility
  const pagesRouter = useContext(RouterContext);
  const appRouterPathname = usePathname();
  const appRouter = useAppRouter();

  const pathname = pagesRouter?.pathname || appRouterPathname || '/';
  const locale = useLocale();
  const { userLoggedOut } = useCustomer();

  if (!locale) throw new HookError('Locale must be specified to logout');

  return useCallback(async () => {
    const response = await logoutFetcher(locale);

    if (response.status === 200) {
      // If on an account page, route to home page
      const accountRegex = new RegExp('/my-account', 'i');
      if (accountRegex.test(pathname)) {
        if (pagesRouter) {
          pagesRouter.push('/');
        } else {
          appRouter.push('/');
        }
      }

      userLoggedOut();
      window?.location.reload();
      return true;
    }

    return false;
  }, [locale, pathname, userLoggedOut, pagesRouter, appRouter]);
};

export default useLogout;
